import React, { useEffect, useCallback, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";

import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Grid from "@material-ui/core/Grid";

import Button from "components/CustomButtons/Button";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import CardText from "components/Card/CardText";

import { actionCreators as actionCreatorsChapter } from "../../../_actions/chapterActions";
import Loading from "../../common/Loading";
import EnhancedTableHead, {
  stableSort,
  getComparator
} from "../../common/EnhancedTableHead";

import { chapterYearList } from "../../../utils";
import MemberCountDialog from "./EditDialog";
import { PAGE_SIZE } from "../../../constants";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%"
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2)
  },
  table: {
    minWidth: 750
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1
  },
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  cursorPointer: {
    cursor: "pointer"
  },
  menuPaper: {
    maxHeight: 300
  },
  formControl: {
    minWidth: 150
  },
  fullWidth: {
    flex: 1
  }
}));

const headCells = [
  {
    id: "year",
    numeric: false,
    disablePadding: false,
    label: "Year"
  },
  {
    id: "count",
    numeric: false,
    disablePadding: false,
    label: "Count"
  },
  {
    id: "updatedDate",
    numeric: false,
    disablePadding: false,
    label: "Date Submitted"
  },
  {
    id: "submittedBy",
    numeric: true,
    disablePadding: false,
    label: "Submitted By"
  }
];

const sortBy = {
  year: 1,
  chapterName: 2,
  count: 3,
  updatedDate: 4,
  submittedBy: 5
};

const MemberCounts = props => {
  const classes = useStyles();
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("year");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    parseInt(localStorage.getItem("chapterMemberCountsPageSize")) || PAGE_SIZE
  );
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setEditing] = useState(false);
  const [selectedRow, setSelectedRow] = useState("");

  const {
    getMemberCounts,
    memberCounts,
    createMemberCount,
    updateMemberCount,
    totalLength,
    isFetched,
    isFetching,
    resetIsFetched,
    user
  } = props;

  useEffect(() => {
    localStorage.setItem("chapterMemberCountsPageSize", rowsPerPage);
  }, [rowsPerPage]);

  useEffect(() => {
    if (!isFetched.MEMBER_COUNTS && !isFetching.MEMBER_COUNTS) {
      getMemberCounts(
        rowsPerPage,
        page,
        sortBy[orderBy],
        order === "desc" ? 1 : 2
      );
    }
  }, [isFetched, isFetching, rowsPerPage, page, orderBy, order]);

  const getYearList = useCallback(() => {
    const lastYear = chapterYearList[chapterYearList.length - 1];
    if (
      !isEditing &&
      memberCounts &&
      memberCounts.find(x => x.year === lastYear)
    ) {
      return chapterYearList.slice(0, chapterYearList.length - 1);
    }
    return chapterYearList;
  }, [memberCounts, isEditing]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    resetIsFetched();
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleEditTopic = useCallback(
    row => () => {
      if (row.editable) {
        setSelectedRow(row);
        setDialogOpen(true);
        setEditing(true);
      }
    },
    []
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleCreateDialogOpen = useCallback(() => {
    setDialogOpen(true);
    setEditing(false);
  }, []);

  const handleDialogSave = useCallback(
    async (fiscalYear, count) => {
      setDialogOpen(false);
      const payload = {
        chapter: user.chapter,
        year: fiscalYear,
        count
      };
      if (isEditing) {
        await updateMemberCount({
          ...payload,
          id: selectedRow.id
        });
      } else {
        await createMemberCount(payload);
      }
      if (!isFetching.MEMBER_COUNTS) {
        getMemberCounts(
          rowsPerPage,
          page,
          sortBy[orderBy],
          order === "desc" ? 1 : 2
        );
      }
    },
    [
      isEditing,
      selectedRow,
      user,
      isFetching,
      rowsPerPage,
      page,
      orderBy,
      order
    ]
  );

  const getStandardDate = useCallback(dateString => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US");
  }, []);

  return (
    <Container>
      <Card>
        <CardHeader color="primary" text>
          <CardText color="primary">
            <Typography variant="h4">
              Chapter Member Count - {user.chapter.name}
            </Typography>
          </CardText>
        </CardHeader>
        <CardBody>
          <Grid
            container
            direction="column"
            className={classes.marginBottom}
            spacing={2}
            justify="flex-end"
          >
            <Grid item>
              <Typography variant="body1">
                Chapter Member Count Information as of September 30th each year
                in the grid. Years are respective Fiscal Years.
              </Typography>
            </Grid>
            <Grid item className={classes.fullWidth}>
              <Button color="success" onClick={handleCreateDialogOpen}>
                Submit Member Count
              </Button>
            </Grid>
          </Grid>
          <TableContainer>
            <Table
              className={classes.table}
              aria-labelledby="tableTitle"
              size="medium"
              aria-label="enhanced table"
            >
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={totalLength}
                headCells={headCells}
              />

              <TableBody>
                {isFetching.MEMBER_COUNTS ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Loading />
                    </TableCell>
                  </TableRow>
                ) : !memberCounts || memberCounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No data
                    </TableCell>
                  </TableRow>
                ) : (
                  stableSort(memberCounts, getComparator(order, orderBy)).map(
                    row => {
                      const labelId = `enhanced-table-row-${row.id}`;

                      return (
                        <TableRow
                          hover
                          className={clsx({
                            [classes.cursorPointer]: row.editable
                          })}
                          onClick={handleEditTopic(row)}
                          role="checkbox"
                          tabIndex={-1}
                          key={labelId}
                        >
                          <TableCell component="th" scope="row">
                            {row.year}
                          </TableCell>
                          <TableCell component="th" scope="row">
                            {row.count}
                          </TableCell>
                          <TableCell>
                            {getStandardDate(row.updatedDate)}
                          </TableCell>
                          <TableCell align="right">
                            {row.updatedByUserName}
                          </TableCell>
                        </TableRow>
                      );
                    }
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalLength}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
          <MemberCountDialog
            open={isDialogOpen}
            editing={isEditing}
            chapterName={user.chapter.name}
            chapterList={getYearList()}
            year={isEditing && selectedRow ? selectedRow.year : ""}
            originalCount={isEditing && selectedRow ? selectedRow.count : ""}
            onClose={handleDialogClose}
            onSave={handleDialogSave}
          />
        </CardBody>
      </Card>
    </Container>
  );
};

const mapStateToProps = state => ({
  isLoading: state.chapterReducer.isLoading,
  memberCounts: state.chapterReducer.memberCounts,
  totalLength: state.chapterReducer.totalMemberCounts,
  isFetched: state.chapterReducer.isFetched,
  isFetching: state.chapterReducer.isFetching,
  user: state.userReducer.user
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actionCreatorsChapter }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(MemberCounts);
